/**
 * La lecture de « Libérer des créneaux » : la semaine (`getAvailability`, rangée par le réducteur
 * `tutorAvailability`), et le créneau du live d'où part le geste.
 *
 * Comme `ReleaseSlotsScreen` : « Ma dispo », dessous, repart de chaque semaine lue — on ne relit
 * donc que s'il n'y a pas de semaine en main, ou si le parcours part d'un live (son créneau se lit
 * sur une semaine fraîche, par le live, jamais par l'heure).
 */
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { tutorAvailabilityService } from "../../../../../../data/services/tutorAvailabilityService"
import { engagedSlotOfLive } from "../../../../adapters/tutor/Availability/tutorReleaseAdapter"
import { type ApiTutorWeekView, engagedSlotsOf, slotKeyOf } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import { useApiFetch } from "../../../hooks/useApiFetch"

type WeekState = { data?: { tutorAvailability?: { weekView?: ApiTutorWeekView | null } | null } }

export function useTutorReleaseSlotsData(
    liveSessionId: number | string | null,
    onLiveSlotFound: (key: string) => void
) {
    const week = useSelector((state: unknown) => (state as WeekState)?.data?.tutorAvailability?.weekView ?? null)
    // Décidé une fois, au montage.
    const fetchOnMountRef = useRef(week == null || liveSessionId != null)
    const [weekFetched, setWeekFetched] = useState(false)
    const { fetch } = useApiFetch({
        service: tutorAvailabilityService.getAvailability,
        fetchOnMount: fetchOnMountRef.current,
        onSuccess: () => setWeekFetched(true),
    })

    const resolvedRef = useRef(false)
    const onFoundRef = useRef(onLiveSlotFound)
    onFoundRef.current = onLiveSlotFound
    useEffect(() => {
        if (liveSessionId == null || !weekFetched || resolvedRef.current) return
        const slot = engagedSlotOfLive(engagedSlotsOf(week), liveSessionId)
        if (!slot?.slot) return
        resolvedRef.current = true
        onFoundRef.current(slotKeyOf(slot.slot))
    }, [liveSessionId, weekFetched, week])

    return { retry: fetch }
}
